/**
 * Browser-storage bookkeeping for the Kore.ai chat widget.
 *
 * The SDK's destroy() does not clear tokens or chat state, so logout cleanup
 * is our responsibility. This module is imported from the global
 * accountDataCache listener and therefore MUST stay free of SDK imports —
 * anything imported here ends up in the shared client bundle of every page.
 */

export const KORE_CHAT_USER_ID_KEY = "koreChatUserId";

// Keys written by @lmn/kore-ai-websdk v11.12.1040 (verified against its dist;
// the co-browse/location keys belong to plugins we do not load, cleared defensively).
const SDK_LOCAL_STORAGE_KEYS = [
  "kr-agent-status",
  "cobrowseRequest",
  "locationData",
  "pagesVisited",
];
const SDK_SESSION_STORAGE_KEYS = ["isReconnect"];

// Set by CIA-BE / the login widget; the chat SDK authenticates with it but
// never removes it itself.
const CIA_AUTH_TOKEN_COOKIE = "ciaAuthToken";

const deleteCookieEverywhere = (name: string): void => {
  const { hostname } = window.location;
  const expiry = "; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/";
  const registrableDomain = hostname.split(".").slice(-2).join(".");
  // Cover host-only, exact-host and registrable-domain (.weg.de) scoped cookies.
  const domainVariants = ["", `; domain=${hostname}`, `; domain=.${registrableDomain}`];
  domainVariants.forEach((domain) => {
    document.cookie = `${name}=${expiry}${domain}`;
  });
};

const generateAnonymousId = (): string =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `anon-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;

/**
 * Stable per-tab-session identity for anonymous chat users. Deliberately
 * sessionStorage (not localStorage): no cross-session tracking identifier is
 * persisted without consent (the old Vivocha integration got this wrong).
 */
export const getOrCreateAnonymousChatUserId = (): string => {
  try {
    const existing = sessionStorage.getItem(KORE_CHAT_USER_ID_KEY);
    if (existing) return existing;
    const id = generateAnonymousId();
    sessionStorage.setItem(KORE_CHAT_USER_ID_KEY, id);
    return id;
  } catch {
    // sessionStorage may be unavailable (private browsing); fall back to a
    // non-persisted id — the chat still works, just without reconnect identity.
    return generateAnonymousId();
  }
};

/** Best-effort removal of everything the chat leaves in the browser. */
export const clearKoreChatData = (): void => {
  // The cookie deletion is the security-critical part (a surviving
  // ciaAuthToken keeps the logged-out user's identity alive), so it runs
  // first and isolated — a storage API throwing must not skip it.
  try {
    deleteCookieEverywhere(CIA_AUTH_TOKEN_COOKIE);
  } catch {
    // document.cookie unavailable; nothing more we can do
  }
  try {
    SDK_LOCAL_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
  } catch {
    // localStorage may be unavailable (private browsing)
  }
  try {
    SDK_SESSION_STORAGE_KEYS.forEach((key) => sessionStorage.removeItem(key));
    sessionStorage.removeItem(KORE_CHAT_USER_ID_KEY);
  } catch {
    // sessionStorage may be unavailable
  }
};

/**
 * One-time cleanup of the persistent identifier the removed Vivocha
 * integration wrote without consent (vivochaHelpers' "userCookie"). Nothing
 * reads it anymore; without this no code path would ever remove it from
 * users' browsers.
 */
export const clearLegacyVivochaData = (): void => {
  try {
    localStorage.removeItem("userCookie");
  } catch {
    // best-effort
  }
};
